import cn from 'classnames';
import Coins from 'components/icons/illustrative/Coins';
import pick from 'lodash/pick';
import { useState, useEffect, useContext, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router';
import { CSSTransition } from 'react-transition-group';
import { useAnalytics } from 'use-analytics';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import { UserContext } from 'components/signUpFunnel/Contexts';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getServingsForDisplay } from 'helpers/servings';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useAssignExperiment from 'hooks/useAssignExperiment';
import useBrand from 'hooks/useBrand';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useFunnelNavigation from 'hooks/useFunnelNavigation';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import {
  FAMILY,
  TEMPO,
  POPULATE_DATA_LAYER,
  ADD_TO_CART_EVENT,
  PAGE_VIEW_EVENT,
} from 'lib/constants';
import { Statuses } from 'lib/constants/signUp';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

import FamilyBoxCustomizer from './FamilyBoxCustomizer';
import MillionsBanner from './MillionsBanner';
import OrderSummary from './OrderSummary';
import OrderSummaryMobile from './OrderSummaryMobile';
import RecommendedMealsTip from './RecommendedMealsTip';
import StandardBoxCustomizer from './StandardBoxCustomizer';
import TempoBoxBanner from './TempoBoxBanner';
import TempoBoxCustomizer from './TempoBoxCustomizer';

const BoxContainer = () => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { brand } = useContext(BrandContext);
  const { currentStep, nextStep } = useFunnelNavigation();
  const isTempo = useBrand(TEMPO);
  const isHomeChef = useBrand(HOME_CHEF);
  const transitionRef = useRef();
  const disclaimerRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [box, setBox] = useState(null);
  const [mealPriceDetails, setMealPriceDetails] = useState(null);
  const [error, setError] = useState();
  const [servings, setServings] = useState();
  const [weeklyMeals, setWeeklyMeals] = useState();
  const [boxRecommendations, setBoxRecommendations] = useState({});
  const [boxSignupAnswers, setBoxSignupAnswers] = useState();
  const { isMobile } = useScreenSizeConstants();
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;
  const experimentAssignment = useFunnelExperimentAssignment();
  const competitivePricingExperiment = isTempo
    ? 'competitive_pricing_copy_tempo_0826'
    : 'competitive_pricing_copy_0826';
  const { isVariant: competitivePricingExperimentGroup } = useAssignExperiment(
    { name: competitivePricingExperiment },
    [brand]
  );

  const competitivePricingDisplay = isTempo
    ? {
        container: 'border-tempo-black-100 bg-tempo-blue-50',
        textColor: 'text-tempo-blue-90',
        leadText: 'Tempo is up to',
        boldText: '15% cheaper than Factor',
        trailText: 'for new customers.',
      }
    : {
        container: 'border-green-120 bg-green-50',
        textColor: 'text-green-120',
        leadText: 'Home Chef is up to',
        boldText: '30% cheaper than Hello Fresh',
        trailText: 'for new customers.',
      };

  const displayServings = getServingsForDisplay(servings, mealPlanSlug);

  const showRecommendedMealsTip = Boolean(
    isHomeChef && boxSignupAnswers && boxSignupAnswers.length
  );

  const containerText = {
    header: 'Your box recommendation',
    subHeader: isTempo
      ? 'Recommended based on your answers. You can add or remove meals before your order is finalized.'
      : 'Your recommendation is for the number of lunches and/or dinners we’ll suggest.',
  };

  useMetadata({
    title: `Step 4: Box | ${brandData(brand)?.displayName}`,
  });

  useEffect(() => {
    if (!box) return;

    analytics.track(POPULATE_DATA_LAYER, {
      orderValue: (box.subtotalCents + box.shippingCents) / 100,
    });
  }, [box]);

  useEffect(() => {
    RequestService.v3.createEvents({ params: { eventName: PAGE_VIEW_EVENT } });
  }, []);

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].box.viewed, experimentAssignment);
      }
    },
    [brand, experimentAssignment]
  );

  useEffect(() => {
    const fetchTieredPricingDetails = async user => {
      try {
        const response = await RequestService.getTieredPricingDetails({
          params: { id: user.mealPricePlan.id },
        });
        if (response.ok) {
          setMealPriceDetails(response.priceTiers);
        } else {
          ErrorLogging.captureException(response.error);
        }
      } catch (e) {
        ErrorLogging.captureException(e.message);
      }
    };

    if (user && isTempo) {
      fetchTieredPricingDetails(user);
    }
  }, [user, isTempo]);

  useEffect(() => {
    if (!user) return;

    setServings(user.servings || 2);
  }, [user]);

  useEffect(() => {
    const fetchRecommendedBox = async () => {
      try {
        const response = await RequestService.getRecommendedBox();
        if (response.ok) {
          setWeeklyMeals(
            user.status === Statuses.BILLING_STEP ? user.weeklyMeals : response.recommendedMeals
          );
          setServings(
            user.status === Statuses.BILLING_STEP
              ? user.servings || 2
              : response.recommendedServings || 2
          );
          setBoxRecommendations({
            recommendedMeals: response.recommendedMeals,
            recommendedServings: response.recommendedServings,
            displayOrder: response.displayOrder,
          });
        } else {
          setWeeklyMeals(user.weeklyMeals);
          setServings(user.servings || 2);
          ErrorLogging.captureInfo(response.error);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    if (!user) return;

    fetchRecommendedBox();
  }, [user, brand]);

  useEffect(() => {
    if (!servings || !weeklyMeals) return;

    const fetchBox = async () => {
      try {
        const response = await RequestService.getBox({
          params: {
            servings,
            weeklyMeals,
            zipCode: user?.signupZip,
            estimated: true,
          },
        });
        if (response.ok) {
          setBox(response.box);
        } else {
          ErrorLogging.captureInfo(response.error);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchBox();
  }, [servings, weeklyMeals, user?.signupZip]);

  useEffect(() => {
    const fetchBoxSignupAnswers = async () => {
      try {
        const response = await RequestService.getBoxSignupAnswers({
          params: { boxRecommendation: { pathname: 'box-meals' } },
        });

        if (response.ok) setBoxSignupAnswers(response.boxSignupAnswers);
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    if (user) fetchBoxSignupAnswers();
  }, [user]);

  const handleContinue = useAnalyticsCallback(
    async () => {
      setIsSubmitting(true);
      let response = {};
      try {
        response = await RequestService.updateBox({
          params: { servings, weeklyMeals, currentStep: currentStep?.status },
        });
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
      setIsSubmitting(false);
      if (response.ok) {
        setUser({ ...user, ...response.user, status: nextStep.status });
        const box = response.box;
        try {
          setBox(prevBox => ({ ...prevBox, ...pick(box, ['editingCutoff', 'deliveryDate']) }));
          analytics.track(ADD_TO_CART_EVENT, { ...box, servings });
        } catch (error) {
          ErrorLogging.captureException(error.message);
        }
        navigate(nextStep.route);
      } else {
        const message = response.error?.message || response.error;
        setError(message);
      }
    },
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].box.submitted, {
          servings: displayServings,
          weeklyMeals,
          doubleUpSelected: user?.servings === 4 && isFamilyMealPlan,
          mealPlanSlug: mealPlanSlug,
          ...experimentAssignment,
        });
      }
    },
    [
      servings,
      weeklyMeals,
      user,
      brand,
      isFamilyMealPlan,
      displayServings,
      mealPlanSlug,
      currentStep,
      nextStep,
      experimentAssignment,
    ]
  );

  if (!user || !box) {
    return (
      <div className="mx-0 mt-24 w-full p-10">
        <section className="flex rounded-md bg-white-100 p-4 bp2:p-6">
          <div className="w-full border-0 border-solid border-charcoal-60 text-center bp2:w-3/4 bp2:border-r bp2:pr-6">
            <Skeleton count={3} className="m-auto my-6 max-w-lg bp2:pr-10" height={32} />
          </div>
          <div className="hidden w-1/4 rounded-md pl-4 bp2:inline">
            <Skeleton count={3} className="my-6" height={32} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <div className="mx-1 w-full max-w-[1020px] px-5 pb-12 bp2:mx-auto bp2:px-4 bp2:pb-0 bp3:max-w-[1085px] bp3:px-0">
        <FunnelHeader
          title={containerText.header}
          subtitle={containerText.subHeader}
          titleClassName="text-left"
          subtitleClassName="mb-6 text-left"
        />
        <div className="flex flex-col gap-6 bp2:flex-row bp2:gap-12 bp3:gap-24">
          <div className="w-full bp2:w-[548px] bp3:w-[576px]">
            <div className="w-full">
              {isHomeChef && isFamilyMealPlan && (
                <FamilyBoxCustomizer
                  weeklyMeals={weeklyMeals}
                  setWeeklyMeals={setWeeklyMeals}
                  servings={servings}
                  setServings={setServings}
                  boxRecommendations={boxRecommendations}
                />
              )}
              {isHomeChef && !isFamilyMealPlan && (
                <StandardBoxCustomizer
                  servings={servings}
                  weeklyMeals={weeklyMeals}
                  setWeeklyMeals={setWeeklyMeals}
                  setServings={setServings}
                  totalRecipes={box?.totalRecipes}
                  potentialSavingsText={box?.potentialSavingsText}
                  boxRecommendations={boxRecommendations}
                />
              )}
              {isTempo ? (
                mealPriceDetails ? (
                  <TempoBoxCustomizer
                    weeklyMeals={weeklyMeals}
                    setWeeklyMeals={setWeeklyMeals}
                    mealPriceDetails={mealPriceDetails}
                    boxRecommendations={boxRecommendations}
                  />
                ) : (
                  <Skeleton count={3} className="my-2" height={26} />
                )
              ) : null}
              <CSSTransition
                in={Boolean(error)}
                classNames="fadeInOut"
                unmountOnExit
                timeout={200}
                nodeRef={transitionRef}
              >
                <div className="py-2" ref={transitionRef}>
                  <Text brandStylesOverrides="textSm text-negative">{error}</Text>
                </div>
              </CSSTransition>

              {competitivePricingExperimentGroup && (
                <div
                  className={cn(
                    'my-4 flex w-full items-center gap-2 rounded-lg border border-solid p-4',
                    competitivePricingDisplay.container,
                    {
                      'fixed bottom-[100px] right-0 mb-0 h-20 w-[-webkit-fill-available] rounded-b-none rounded-t-3xl border-b-0 pt-0':
                        isMobile,
                      'h-auto': !isMobile,
                    }
                  )}
                >
                  {isMobile ? '' : <Coins />}
                  <Text
                    className={cn('text-left font-normal', competitivePricingDisplay.textColor)}
                    brandStylesOverrides="textMd"
                  >
                    {competitivePricingDisplay.leadText}{' '}
                    <strong>{competitivePricingDisplay.boldText} </strong>
                    {competitivePricingDisplay.trailText}
                    <button
                      type="button"
                      onClick={() =>
                        disclaimerRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        })
                      }
                      className={cn(
                        'cursor-pointer border-0 bg-transparent p-0',
                        competitivePricingDisplay.textColor
                      )}
                      aria-label="View pricing disclaimer"
                    >
                      *
                    </button>
                  </Text>
                </div>
              )}
              <div className="text-center">
                <Button
                  type="submit"
                  className="mt-8 hidden w-full items-center justify-center bp2:flex"
                  onClick={handleContinue}
                  isSubmitting={isSubmitting}
                  variation="Pill"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
          <div className="hidden bp2:block bp2:w-[380px] bp3:w-[413px]">
            <OrderSummary
              servings={displayServings}
              weeklyMeals={weeklyMeals}
              box={box}
              boxSignupAnswers={boxSignupAnswers}
              showRecommendedMealsTip={showRecommendedMealsTip}
            />
          </div>
        </div>

        {competitivePricingExperimentGroup && isMobile && (
          <RecommendedMealsTip boxSignupAnswers={boxSignupAnswers} />
        )}

        <div className={cn({ 'mt-[10vh]': isMobile })}>
          {isTempo ? <TempoBoxBanner /> : <MillionsBanner />}
        </div>

        {competitivePricingExperimentGroup && (
          <div ref={disclaimerRef} className="scroll-mt-24 pb-32 pt-6">
            <Text className="text-left text-charcoal-90" brandStylesOverrides="textSm">
              * Pricing may vary based on the zip code and excludes shipping charges and discounts.
            </Text>
          </div>
        )}
      </div>
      <OrderSummaryMobile
        servings={displayServings}
        weeklyMeals={weeklyMeals}
        handleContinue={handleContinue}
        box={box}
        isSubmitting={isSubmitting}
        error={error}
      />
    </>
  );
};

export default BoxContainer;
